/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Fase 2 mobile: barra fija + bottom sheet. Desktop no monta este componente.
 */
import React, { type ReactNode } from 'react';
import { Modal } from '../../shared/modals/Modal';
import {
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import Toast from 'react-native-toast-message';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { appToastConfig, toastTopOffset } from '../../shared/ui/appToast';
import { SoftPressable } from '../../shared/ui/SoftPressable';
import { styles } from './posShared.styles';
import { formatPosMoney } from '../application/posMoneyFormat';

type PosMobileCartBarProps = {
  itemCount: number;
  total: number;
  hasParkedSale: boolean;
  canEdit: boolean;
  isSaving: boolean;
  onOpenSheet: () => void;
};

export function PosMobileCartBar({
  itemCount,
  total,
  hasParkedSale,
  canEdit,
  isSaving,
  onOpenSheet,
}: PosMobileCartBarProps) {
  const insets = useSafeAreaInsets();
  const cartEmpty = itemCount === 0;
  const itemCountLabel = itemCount === 1 ? '1 \u00edtem' : `${itemCount} \u00edtems`;
  const summaryText = cartEmpty ? 'Carrito vac\u00edo' : itemCountLabel;
  const totalText = cartEmpty ? '$0' : formatPosMoney(total);
  const a11yLabel = hasParkedSale
    ? `${summaryText}. Hay 1 venta pausada`
    : summaryText;

  return (
    <View
      style={[
        styles.posMobileCartBar,
        { paddingBottom: Math.max(insets.bottom, spacing.sm) },
      ]}
    >
      <SoftPressable
        style={cartBarStyles.cartInfo}
        onPress={onOpenSheet}
        accessibilityRole="button"
        accessibilityLabel={a11yLabel}
        testID="pos-mobile-cart-open-sheet"
      >
        <View style={cartBarStyles.cartCountRow}>
          <Text
            style={[cartBarStyles.cartCount, cartEmpty && cartBarStyles.cartCountEmpty]}
            numberOfLines={1}
          >
            {summaryText}
          </Text>
          {hasParkedSale ? (
            <View style={cartBarStyles.parkedChip} accessibilityElementsHidden>
              <Text style={cartBarStyles.parkedChipText}>1 pausada</Text>
            </View>
          ) : null}
        </View>
        <Text
          style={[cartBarStyles.cartTotal, cartEmpty && cartBarStyles.cartTotalEmpty]}
          numberOfLines={1}
        >
          {totalText}
        </Text>
      </SoftPressable>
      <SoftPressable
        style={[
          cartBarStyles.cartBtn,
          cartEmpty && !hasParkedSale && cartBarStyles.cartBtnDisabled,
        ]}
        onPress={onOpenSheet}
        disabled={(cartEmpty && !hasParkedSale && !canEdit) || isSaving}
        hoverLift={false}
        accessibilityRole="button"
        accessibilityLabel="Ver carrito"
        testID="pos-mobile-cart-view-cart"
      >
        <Text style={cartBarStyles.cartBtnText}>Ver carrito</Text>
        <Ionicons name="caret-up" size={14} color={ticketColors.goldInk} />
      </SoftPressable>
    </View>
  );
}

const cartBarStyles = StyleSheet.create({
  cartInfo: {
    flex: 1,
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  cartCountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },
  cartCount: {
    flexShrink: 1,
    fontSize: 12,
    color: ticketColors.ink3,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  cartCountEmpty: {
    color: ticketColors.ink3,
  },
  parkedChip: {
    flexShrink: 0,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    paddingVertical: 3,
    paddingHorizontal: 6,
  },
  parkedChipText: {
    fontSize: 10,
    fontWeight: '600',
    lineHeight: 12,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  cartTotal: {
    fontSize: 20,
    fontWeight: '700',
    color: ticketColors.ink,
    fontVariant: ['tabular-nums'],
    letterSpacing: -0.4,
    fontFamily: ticketFonts.mono,
  },
  cartTotalEmpty: {
    color: ticketColors.ink3,
  },
  cartBtn: {
    backgroundColor: ticketColors.gold,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minHeight: 44,
  },
  cartBtnDisabled: {
    opacity: 0.4,
  },
  cartBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
});

type PosMobileCartSheetProps = {
  visible: boolean;
  onClose: () => void;
  itemCount?: number;
  headerRight?: ReactNode;
  children: ReactNode;
};

export function PosMobileCartSheet({
  visible,
  onClose,
  itemCount = 0,
  headerRight,
  children,
}: PosMobileCartSheetProps) {
  const insets = useSafeAreaInsets();
  const { height } = useWindowDimensions();
  const sheetMaxHeight = Math.min(height * 0.86, height - insets.top - spacing.lg);
  const title = itemCount > 0 ? `Ticket \u00b7 ${itemCount}` : 'Ticket';

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.posMobileCartSheetBackdrop}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        <View
          style={[
            styles.posMobileCartSheetPanel,
            {
              height: sheetMaxHeight,
              maxHeight: sheetMaxHeight,
              paddingBottom: Math.max(insets.bottom, spacing.md),
            },
          ]}
        >
          <View style={styles.posMobileCartSheetHandle} />
          <View style={sheetStyles.sheetHeader}>
            <Text style={sheetStyles.sheetTitle}>{title}</Text>
            <View style={sheetStyles.sheetHeaderRight}>
              {headerRight}
              <TouchableOpacity
                onPress={onClose}
                style={sheetStyles.closeBtn}
                activeOpacity={0.6}
                accessibilityRole="button"
                accessibilityLabel="Cerrar carrito"
              >
                <Ionicons name="close" size={20} color={ticketColors.ink2} />
              </TouchableOpacity>
            </View>
          </View>
          <View style={sheetStyles.sheetBody}>{children}</View>
        </View>
        {/* RN Modal nativo tapa el Toast global: host local para avisos (p. ej. stock).
            Desmontar al cerrar para no capturar Toast.show de pantallas debajo. */}
        {visible ? <Toast config={appToastConfig} topOffset={toastTopOffset(insets.top)} /> : null}
      </View>
    </Modal>
  );
}

const sheetStyles = StyleSheet.create({
  sheetHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 8,
    paddingHorizontal: 2,
    flexShrink: 0,
  },
  sheetHeaderRight: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 4,
  },
  sheetTitle: {
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 0.52,
    textTransform: 'uppercase',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sheetBody: {
    width: '100%',
    flex: 1,
    minHeight: 0,
  },
});
